import { Col, Modal, Row, Spin } from 'antd';
import classNames from 'classnames';
import AlertsSearch from 'Components/alerts/components/AlertsSearch';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  appendSalesforceEmailDomainToSearchTerm,
  backgroundColorParsing, getLangCookie, validateSearchQuery,
} from 'Utils/UtilityFunctions';
import { useNavigate } from 'react-router-dom';
import { COMPANY_OVERVIEW_ROUTE, PERSONALITY_INSIGHTS_ROUTE } from 'Utils/Constants';
import { Executive } from 'Types/PersonTypes';
import { SearchResultsRequest } from 'Types/GlobalSearchResultsTypes';
import { selectScreenName } from 'Selector/GlobalSearchContentSelector';
import { selectLoading, selectPersonLoaded, peopleData } from 'Selector/GlobalSearchResultsSelector';
import { Company } from 'Types/CompaniesTypes';
import NoDataFound from '../components/NoDataFound';
import PeopleCardLarge from '../components/PeopleCardLarge';
import { actions as searchActions } from '../../../Slice/GlobalSearchResultsSlice';
import CompanyCardLarge from '../components/CompanyCardLarge';
import ExtendedSearchLoader from '../components/ExtendedSearchLoader';

const BottomLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 0 16px;
`;

const CenterLoader = styled.div`
  width: 100%;
  height: 100%;
  min-height: inherit;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PopupLoader = function PopupLoader() {
  return (
    <BottomLoader className="search-result-bottom-loader">
      <Spin size="default" />
    </BottomLoader>
  );
};

const PopupCenterLoader = function PopupCenterLoader() {
  return (
    <CenterLoader className="search-result-center-loader">
      <Spin size="large" />
    </CenterLoader>
  );
};

const ExtendedSearchCol = function ExtendedSearchCol() {
  return (
    <Col md={{ span: 6 }} className="person-comparison-col outer-search">
      <ExtendedSearchLoader />
    </Col>
  );
};

const NoDataWrapper = styled.div`
  width: 100%;
  .no_data_found {
    height: calc(-542px + 100vh) !important;
  }
`;

const SearchWrapper = styled.div`
  width: 100%;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  .ant-input-affix-wrapper {
    height: 40px;
    margin-bottom: 0;
    padding-right: 36px;
    .ant-input-prefix {
      .anticon-search {
        font-size: 17px;
      }
    }
  }
  .search-wrapper {
    position: relative;
    > div {
      position: relative;
      width: 100%;
    }
    .cross-icon {
      position: absolute;
      top: 50% !important;
      right: 14px !important;
      transform: translateY(-50%);
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
  }
`;

type Props = {
  type: 'person' | 'company';
  count: number;
  searchQuery: string;
  isVisible: boolean
  setIsVisible: (value: boolean) => void
  persons?: Executive[];
  companies?: Company[];
  enableSearchBar?: boolean;
  buildSearchQuery?: (searchTerm: string, options?: { includeEmailDomain?: boolean }) => string;
  initialManualSearch?: boolean;
} & typeof defaultProps;

export type SalesforcePersonSearchNavigationState = {
  from?: string;
  salesforceSearchQuery?: string;
  salesforceSearchHasManual?: boolean;
};

const defaultProps = {
  persons: [] as Executive[],
  companies: [] as Company[],
  enableSearchBar: false,
  buildSearchQuery: undefined as ((searchTerm: string, options?: { includeEmailDomain?: boolean }) => string) | undefined,
  initialManualSearch: false,
};

const SearchResultPopup = function SearchResultPopup(props: Props) {
  const {
    type, count, searchQuery, persons, companies, isVisible, setIsVisible,
    enableSearchBar, buildSearchQuery, initialManualSearch,
  } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [page, setPage] = useState<number>(2);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const searchLoading = useSelector(selectLoading);
  const personLoaded = useSelector(selectPersonLoaded);
  const peopleSection = useSelector(peopleData);
  const screenName = useSelector(selectScreenName);
  const scrollDivRef = useRef<HTMLDivElement>(null);
  const activeQueryRef = useRef('');
  const hasManualSearchRef = useRef(false);
  const isLoading = searchLoading;
  const isPerson = type === 'person';
  const resultCount = enableSearchBar && isPerson
    ? (peopleSection?.count ?? 0)
    : count;

  const getDisplaySearchQuery = useCallback((term: string) => {
    if (enableSearchBar && !hasManualSearchRef.current) {
      return appendSalesforceEmailDomainToSearchTerm(term);
    }
    return term;
  }, [enableSearchBar]);

  const formatQuery = useCallback((term: string, isManualSearch = false) => {
    if (buildSearchQuery) {
      return buildSearchQuery(term, { includeEmailDomain: !isManualSearch });
    }
    return isPerson ? `person:${term}` : `company:${term}`;
  }, [buildSearchQuery, isPerson]);

  const runSearch = useCallback((term: string, searchPage = 1, isManualSearch = false) => {
    const trimmedTerm = validateSearchQuery(term.trim());
    if (!trimmedTerm) {
      return;
    }

    if (isManualSearch) {
      hasManualSearchRef.current = true;
    }

    const formattedQuery = formatQuery(trimmedTerm, isManualSearch);
    activeQueryRef.current = formattedQuery;

    scrollDivRef.current?.scrollTo({ top: 0 });
    setLocalSearchQuery(trimmedTerm);
    setPage(2);
    setActiveIndex(-1);

    if (enableSearchBar && searchPage === 1) {
      dispatch(searchActions.resetPeopleSearchSection());
    }

    const obj: SearchResultsRequest = {
      query: formattedQuery,
      screenName,
      page: searchPage,
      defaultLanguage: getLangCookie(),
      ...(enableSearchBar && searchPage > 1 ? { skipExtendedSearch: true } : {}),
    };
    dispatch(searchActions.globalSearchResults(obj));
  }, [dispatch, enableSearchBar, formatQuery, screenName]);

  useEffect(() => {
    hasManualSearchRef.current = Boolean(initialManualSearch);
  }, [searchQuery, initialManualSearch]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    const displayTerm = getDisplaySearchQuery(searchQuery);
    setLocalSearchQuery(displayTerm);
    setPage(2);
    activeQueryRef.current = formatQuery(displayTerm, false);
    // Only sync when popup opens or URL search term changes (initial contact-list landing).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, searchQuery]);

  const fetchMore = () => {
    const obj: SearchResultsRequest = {
      query: formatQuery(localSearchQuery, hasManualSearchRef.current),
      screenName,
      page,
      defaultLanguage: getLangCookie(),
      ...(enableSearchBar ? { skipExtendedSearch: true } : {}),
    };
    setPage(page + 1);
    dispatch(searchActions.globalSearchResults(obj));
  };

  const onSearching = (term: string) => {
    runSearch(term, 1, true);
  };

  const showExtendedSearchLoader = enableSearchBar
    && isPerson
    && !isLoading
    && !personLoaded
    && activeQueryRef.current === formatQuery(localSearchQuery, hasManualSearchRef.current);

  const hasVisibleResults = isPerson ? persons.length > 0 : companies.length > 0;

  const showCenterSpinLoader = enableSearchBar && isLoading && !hasVisibleResults;
  const showInlineExtendedLoader = showExtendedSearchLoader;
  const showBottomSpinLoader = isLoading && hasVisibleResults;

  const handleClick = (index: number, id: number, personType?: string) => {
    setIsVisible(false);
    setActiveIndex(index);
    if (isPerson) {
      const navigationState: SalesforcePersonSearchNavigationState = enableSearchBar
        ? {
          from: 'salesforce-person-search',
          salesforceSearchQuery: localSearchQuery,
          salesforceSearchHasManual: hasManualSearchRef.current,
        }
        : { from: 'salesforce-person-search' };
      navigate(
        `${PERSONALITY_INSIGHTS_ROUTE}${id}&personType=${personType || 'EXECUTIVE'}`,
        { replace: true, state: navigationState },
      );
    } else {
      navigate(`${COMPANY_OVERVIEW_ROUTE}${id}`, { replace: true });
    }
  };

  const getPersonInfiniteScroll = () => (
    <InfiniteScroll
      hasMore
      dataLength={persons.length}
      next={fetchMore}
      className="o-x-hidden infinite-scroll-div mr-1 person-comparison-row"
      loader={false}
      height="auto"
    >
      <Row gutter={[16, 16]} className="comparison-grid">
        {persons.length ? persons.map((person, key) => (
          <Col md={{ span: 6 }} className="person-comparison-col" key={person.personId}>
            <div
              role="button"
              tabIndex={0}
              className={classNames('card', { activeCard: key === activeIndex })}
              onClick={() => handleClick(key, person.personId, person.personType || '')}
              onKeyPress={() => handleClick(key, person.personId, person.personType || '')}
            >
              <PeopleCardLarge
                diameter="130px"
                diameterXxsm="125px"
                imageDiameter="130px"
                holoDiameterWidth="125px"
                holoDiameterHeight="115px"
                holoDiameterXxsmHeight="110px"
                holoDiameterXxsmWidth="120px"
                key={person?.personId}
                persona={person?.archeType?.color}
                logo={person?.personImage}
                personName={person?.personName}
                tag={person?.personDesignation}
                personaType={person?.archeType?.name}
                wrapperColor={backgroundColorParsing(person?.personalityTypes)}
                personalityTypesColors={person?.personalityTypes}
                hidePersonality={false}
                personId={person?.personId}
                company={person?.companyName}
                follow={!person?.personAdded}
                disableNavigation
                personAddedFlag={person.personAdded}
              />
            </div>
          </Col>
        )) : null}
        {showInlineExtendedLoader ? <ExtendedSearchCol /> : null}
        {showBottomSpinLoader ? <PopupLoader /> : null}
        {!showBottomSpinLoader && !showInlineExtendedLoader && personLoaded && !persons?.length ? (
          <NoDataWrapper><NoDataFound title="No Record Found" /></NoDataWrapper>
        ) : null}
      </Row>
    </InfiniteScroll>
  );

  const getCompanyInfiniteScroll = () => (
    <InfiniteScroll
      hasMore
      dataLength={companies.length}
      next={fetchMore}
      className="o-x-hidden infinite-scroll-div mr-1 person-comparison-row"
      loader={false}
      height="auto"
    >
      <Row gutter={[16, 16]} className="comparison-grid">
        {companies.length ? companies.map((company, key) => (
          <Col
            key={company.companyId}
            xxl={{ span: 8 }}
            xl={{ span: 8 }}
            lg={{ span: 6 }}
            md={{ span: 6 }}
            sm={{ span: 12 }}
            xs={{ span: 12 }}
            className="company-comparison-col"
          >
            <div
              role="button"
              tabIndex={0}
              className={classNames('card', { activeCard: key === activeIndex })}
              onClick={() => handleClick(key, company.companyId)}
              onKeyPress={() => handleClick(key, company.companyId)}
            >
              <CompanyCardLarge
                key={company?.companyId}
                companyData={company}
                showDrawerIcon={false}
                showSapIcon={false}
                isRecomended
              />
            </div>
          </Col>
        )) : null}
        {isLoading ? <PopupLoader /> : null}
        {!isLoading && !companies?.length ? <NoDataWrapper><NoDataFound title="No Record Found" /></NoDataWrapper> : null}
      </Row>
    </InfiniteScroll>
  );

  return (
    <Modal
      open={isVisible}
      centered
      footer={null}
      width={910}
      wrapClassName={classNames('profile-comparison-popup', { 'search-result-popup': enableSearchBar })}
      getContainer=""
      closable={false}
      maskStyle={{ backgroundColor: 'rgba(0, 23, 96, 0.18)', backdropFilter: 'blur(8px)' }}
    >
      {enableSearchBar ? (
        <>
          <div className="search-result-header">
            <div className="header-text">
              <h2 className="header-title">
                {`Search Results (${resultCount})`}
              </h2>
              {localSearchQuery ? (
                <p className="header-subtitle">
                  Showing matches for
                  {' '}
                  <span className="query-highlight">{localSearchQuery}</span>
                </p>
              ) : null}
            </div>
          </div>
          <SearchWrapper className="search-result-search-section">
            <AlertsSearch
              onSearching={onSearching}
              placeholderText={isPerson ? 'Search people...' : 'Search companies...'}
              margin="0px"
              query={localSearchQuery}
              loading={isLoading}
              height="40px"
            />
          </SearchWrapper>
        </>
      ) : (
        <span className="popup-heading">
          {`Results (${resultCount})`}
        </span>
      )}
      <div id="person-comparison-row" ref={scrollDivRef} className="w-100 h-100">
        {showCenterSpinLoader ? (
          <PopupCenterLoader />
        ) : isPerson ? (
          getPersonInfiniteScroll()
        ) : (
          getCompanyInfiniteScroll()
        )}
      </div>
      {enableSearchBar && hasVisibleResults && !showCenterSpinLoader ? (
        <div className="search-result-footer">
          Click a profile to view personality insights
        </div>
      ) : null}
    </Modal>
  );
};
SearchResultPopup.defaultProps = defaultProps;
export default SearchResultPopup;
